import { useEffect, useState } from "react";
import api from "@/lib/api";
import DataTable from "@/components/admin/DataTable";
import SearchBox from "@/components/admin/SearchBox";
import EmptyState from "@/components/admin/EmptyState";
import StatusBadge from "@/components/admin/StatusBadge";
import ExportCSVButton from "@/components/admin/ExportCSVButton";
import { FileText } from "lucide-react";
import AdminProjectDetail from "./AdminProjectDetail";

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-AU");
}

function tone(status) {
  if (status === "active") return "grey";
  if (status === "unlocked") return "green";
  if (status === "archived") return "amber";
  if (status === "deleted") return "red";
  return "grey";
}

const STATUSES = [
  { v: "active",   l: "Active" },
  { v: "unlocked", l: "Unlocked" },
  { v: "archived", l: "Archived" },
  { v: "deleted",  l: "Deleted" },
  { v: "all",      l: "All" },
];

const COLS = [
  { key: "title", label: "Title", render: (r) => (
    <div className="min-w-0">
      <div className="truncate text-sm text-brand-ink">{r.title}</div>
      <div className="text-xs text-brand-muted font-mono truncate">{r.owner_email || "—"}</div>
    </div>
  )},
  { key: "status", label: "Status", render: (r) => (
    <StatusBadge tone={tone(r.status)} label={r.status} />
  )},
  { key: "renders_count", label: "Renders", className: "text-right" },
  { key: "schemes_count", label: "Schemes", className: "text-right" },
  { key: "unlocked", label: "Unlock", render: (r) => (
    r.unlocked
      ? <span className="text-xs text-brand-teal">{r.unlocked_by_admin ? "Manual" : "Paid"}</span>
      : <span className="text-xs text-brand-muted">—</span>
  )},
  { key: "updated_at", label: "Updated", render: (r) => relTime(r.updated_at) },
];

export default function AdminProjects() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("active");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (selectedId) return;
    setLoading(true);
    const params = new URLSearchParams({
      page: String(page), page_size: String(pageSize), status,
    });
    if (q) params.set("q", q);
    api.get(`/admin/projects?${params.toString()}`)
      .then((r) => { setData(r.data); setErr(""); })
      .catch((e) => setErr(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, [page, pageSize, q, status, selectedId]);

  if (selectedId) {
    return (
      <AdminProjectDetail
        projectId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  return (
    <div data-testid="admin-projects">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
        <div>
          <h2 className="font-serif text-3xl text-brand-ink tracking-tight">Projects</h2>
          <div className="text-xs text-brand-muted mt-1">
            {data.total} {status === "all" ? "total" : status} project{data.total === 1 ? "" : "s"}
          </div>
        </div>
        <ExportCSVButton
          path="/admin/projects/export.csv"
          params={{ q, status: status !== "all" ? status : undefined }}
          filename="colourmatch-projects.csv"
          testId="projects-export-csv"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <SearchBox
          value={q}
          onChange={(v) => { setQ(v); setPage(1); }}
          placeholder="Search title or owner email…"
          testId="projects-search"
        />

        <div className="inline-flex gap-px bg-brand-linen border border-brand-linen" data-testid="projects-status-pills">
          {STATUSES.map((s) => (
            <button
              key={s.v}
              data-testid={`projects-status-${s.v}`}
              onClick={() => { setStatus(s.v); setPage(1); }}
              className={`px-3 py-1.5 text-xs ${
                status === s.v ? "bg-brand-navy text-white" : "bg-white hover:bg-brand-linen/30"
              }`}
            >
              {s.l}
            </button>
          ))}
        </div>
      </div>

      {err && (
        <div className="text-sm border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3 mb-4">
          {err}
        </div>
      )}

      <DataTable
        testId="projects-table"
        columns={COLS}
        rows={data.items}
        total={data.total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        onRowClick={(row) => setSelectedId(row.id)}
        loading={loading}
        empty={(
          <EmptyState
            icon={FileText}
            title={`No ${status === "all" ? "" : status} projects`}
            caption="Try changing the status filter or clearing the search."
            testId="projects-empty"
          />
        )}
      />
    </div>
  );
}
